import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Buildings, PlusCircle, Trash, PencilSimple, ArrowRight, Lock, Coins, CalendarBlank, X } from "@phosphor-icons/react";

export default function AsesoriaEmpresas() {
    const { user } = useAuth();
    const [saldo, setSaldo] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | {id?, razon_social, cif, ...}

    useEffect(() => {
        loadAll();
    }, []);

    async function loadAll() {
        setLoading(true);
        try {
            const [s, e] = await Promise.all([
                api.get("/asesoria/saldo"),
                api.get("/asesoria/empresas"),
            ]);
            setSaldo(s.data);
            setEmpresas(e.data?.items || []);
        } catch (err) {
            setError(formatApiError(err, "Error al cargar tu asesoría"));
        } finally {
            setLoading(false);
        }
    }

    async function handleSave(form) {
        setError("");
        try {
            const payload = {
                razon_social: form.razon_social,
                cif: form.cif || null,
                contacto_nombre: form.contacto_nombre || null,
                contacto_email: form.contacto_email || null,
                contacto_telefono: form.contacto_telefono || null,
                notas: form.notas || null,
            };
            if (form.id) {
                await api.put(`/asesoria/empresas/${form.id}`, payload);
            } else {
                await api.post("/asesoria/empresas", payload);
            }
            setEditing(null);
            await loadAll();
        } catch (err) {
            setError(formatApiError(err, "Error al guardar"));
        }
    }

    async function handleDelete(id, razon) {
        if (!window.confirm(`¿Borrar "${razon}"? Los análisis ya generados se mantienen.`)) return;
        setError("");
        try {
            await api.delete(`/asesoria/empresas/${id}`);
            await loadAll();
        } catch (err) {
            setError(formatApiError(err, "Error al borrar"));
        }
    }

    const total = saldo?.total_credits || 0;
    const isAsesoria = !!saldo?.is_asesoria;
    const nextExp = saldo?.next_expires_at ? new Date(saldo.next_expires_at) : null;

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-6xl mx-auto w-full px-6 md:px-12 py-12">
                <div className="mb-10" data-testid="asesoria-header">
                    <div className="micro-label mb-3">— Panel Asesoría</div>
                    <div className="flex items-start gap-5">
                        {user?.logo_url && (
                            <img
                                src={user.logo_url}
                                alt={`Logo ${user?.name || "Asesoría"}`}
                                className="h-16 md:h-20 w-auto object-contain bg-white p-2 border border-[var(--gf-border)] flex-shrink-0"
                                data-testid="asesoria-logo"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold mb-1" data-testid="asesoria-name">
                                {user?.name || user?.email || "Asesoría"}
                            </div>
                            <h1 className="serif text-4xl md:text-5xl mb-3 leading-[1.1]" data-testid="asesoria-title">
                                Mis empresas <span className="italic text-[var(--gf-gold)]">vinculadas</span>
                            </h1>
                            <p className="text-sm text-[var(--gf-text-secondary)] max-w-2xl leading-relaxed">
                                Gestiona los clientes para los que emites informes de balance. Cada análisis se cobra
                                de tus créditos unificados (Balance Básico 24 € · Balance Master 44 €).
                            </p>
                        </div>
                    </div>
                </div>

                {/* Widget de saldo */}
                <div className="grid md:grid-cols-3 gap-4 mb-10">
                    <div className="gf-card-ficha border-t-4 border-t-[var(--gf-gold)]" data-testid="saldo-creditos">
                        <Coins size={24} weight="light" className="text-[var(--gf-gold)] mb-3" />
                        <div className="micro-label mb-2">Créditos disponibles</div>
                        <div className="editorial-num">{typeof total === "number" ? `${total.toFixed(2)} €` : total}</div>
                        <div className="text-xs text-[var(--gf-text-muted)] mt-2">
                            {(!total || total === 0) ? "Recarga para empezar" : "Consumibles en cualquier módulo"}
                        </div>
                    </div>
                    <div className="gf-card-ficha border-t-4 border-t-[var(--gf-navy)]" data-testid="saldo-empresas">
                        <Buildings size={24} weight="light" className="text-[var(--gf-navy)] mb-3" />
                        <div className="micro-label mb-2">Empresas vinculadas</div>
                        <div className="editorial-num">{empresas.length}</div>
                        <div className="text-xs text-[var(--gf-text-muted)] mt-2">
                            Sin límite — añade las que necesites
                        </div>
                    </div>
                    <div className="gf-card-ficha border-t-4 border-t-[var(--gf-border)]" data-testid="saldo-caducidad">
                        <CalendarBlank size={24} weight="light" className="text-[var(--gf-text-muted)] mb-3" />
                        <div className="micro-label mb-2">Próxima caducidad</div>
                        <div className="text-xl serif">
                            {nextExp ? nextExp.toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
                        </div>
                        <div className="text-xs text-[var(--gf-text-muted)] mt-2">
                            {nextExp ? "del primer pack que caduca" : "Sin packs activos"}
                        </div>
                    </div>
                </div>

                {/* Si no tiene créditos, CTA para recargar */}
                {!isAsesoria && total === 0 && (
                    <div className="border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-navy)] text-white p-6 mb-10" data-testid="cta-comprar-pack">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
                            <div className="flex items-start gap-3">
                                <Lock size={24} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-1" />
                                <div>
                                    <div className="micro-label text-[var(--gf-gold)] mb-1">— Sin créditos disponibles</div>
                                    <h3 className="serif text-xl">Recarga créditos desde 100 € · Balance Básico 24 €</h3>
                                    <p className="text-sm text-white/75 mt-1">Recarga tus créditos y consúmelos donde quieras: balances, OCR de facturas, modificaciones o Verifactu. Sin caducidad.</p>
                                </div>
                            </div>
                            <Link to="/pagos" className="btn-gold whitespace-nowrap" data-testid="link-comprar">
                                Recargar <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-6" data-testid="ase-error">
                        {error}
                    </div>
                )}

                {/* Tabla empresas + botón añadir */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="serif text-2xl">Empresas ({empresas.length})</h2>
                    <button
                        onClick={() => setEditing({ razon_social: "", cif: "", contacto_nombre: "", contacto_email: "", contacto_telefono: "", notas: "" })}
                        className="btn-gold"
                        data-testid="add-empresa-btn"
                    >
                        <PlusCircle size={16} weight="bold" /> Añadir empresa
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-[var(--gf-text-muted)]">
                        <span className="elegant-loader"><span /><span /><span /></span>
                    </div>
                ) : empresas.length === 0 ? (
                    <div className="gf-card text-center py-12 text-[var(--gf-text-muted)]">
                        <Buildings size={48} weight="light" className="mx-auto mb-3 text-[var(--gf-border)]" />
                        <div className="text-sm">Aún no has añadido ninguna empresa cliente.</div>
                        <div className="text-xs mt-1">Pulsa "Añadir empresa" para empezar.</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="data-table w-full" data-testid="empresas-table">
                            <thead>
                                <tr>
                                    <th>Razón social</th>
                                    <th>CIF</th>
                                    <th>Contacto</th>
                                    <th>Email</th>
                                    <th>Teléfono</th>
                                    <th className="text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {empresas.map((e) => (
                                    <tr key={e.id} data-testid={`empresa-row-${e.id}`}>
                                        <td className="font-semibold">{e.razon_social}</td>
                                        <td className="mono text-xs">{e.cif || "—"}</td>
                                        <td className="text-sm text-[var(--gf-text-secondary)]">{e.contacto_nombre || "—"}</td>
                                        <td className="text-sm">
                                            {e.contacto_email
                                                ? <a href={`mailto:${e.contacto_email}`} className="hover:text-[var(--gf-gold)]">{e.contacto_email}</a>
                                                : <span className="text-[var(--gf-text-muted)]">—</span>}
                                        </td>
                                        <td className="text-sm mono text-[var(--gf-text-secondary)]">{e.contacto_telefono || "—"}</td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button onClick={() => setEditing(e)} className="text-[var(--gf-navy)] hover:text-[var(--gf-gold)]" title="Editar" data-testid={`edit-${e.id}`}>
                                                    <PencilSimple size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(e.id, e.razon_social)} className="text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)]" title="Borrar" data-testid={`delete-${e.id}`}>
                                                    <Trash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Modal edición */}
                {editing && <EmpresaModal value={editing} onCancel={() => setEditing(null)} onSave={handleSave} />}
            </main>
            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function EmpresaModal({ value, onCancel, onSave }) {
    const [form, setForm] = useState({
        id: value.id,
        razon_social: value.razon_social || "",
        cif: value.cif || "",
        contacto_nombre: value.contacto_nombre || "",
        contacto_email: value.contacto_email || "",
        contacto_telefono: value.contacto_telefono || "",
        notas: value.notas || "",
    });
    const [saving, setSaving] = useState(false);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function handle(e) {
        e.preventDefault();
        if (!form.razon_social.trim()) return;
        setSaving(true);
        await onSave(form);
        setSaving(false);
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--gf-navy)]/70 backdrop-blur-sm px-4 py-6 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
            data-testid="empresa-modal"
        >
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="bg-white max-w-xl w-full shadow-2xl border-t-4 border-t-[var(--gf-gold)]"
                onClick={(e) => e.stopPropagation()}
            >
                <form onSubmit={handle} className="p-6 md:p-8">
                    <div className="flex items-start justify-between mb-5">
                        <div>
                            <div className="micro-label text-[var(--gf-gold)] mb-1">— {form.id ? "Editar" : "Nueva"} empresa cliente</div>
                            <h2 className="serif text-2xl text-[var(--gf-navy)]">
                                {form.id ? form.razon_social : "Añadir empresa"}
                            </h2>
                        </div>
                        <button type="button" onClick={onCancel} disabled={saving} className="text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)]" data-testid="modal-close">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <Field label="Razón social *" value={form.razon_social} onChange={(v) => set("razon_social", v)} testId="emp-razon" required />
                        <Field label="CIF / NIF" value={form.cif} onChange={(v) => set("cif", v)} testId="emp-cif" placeholder="B12345678" />
                        <Field label="Persona de contacto" value={form.contacto_nombre} onChange={(v) => set("contacto_nombre", v)} testId="emp-contacto" />
                        <Field label="Email" type="email" value={form.contacto_email} onChange={(v) => set("contacto_email", v)} testId="emp-email" />
                        <Field label="Teléfono" type="tel" value={form.contacto_telefono} onChange={(v) => set("contacto_telefono", v)} testId="emp-tel" />
                    </div>
                    <div className="mb-6">
                        <label className="block text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1.5">Notas internas</label>
                        <textarea
                            value={form.notas}
                            onChange={(e) => set("notas", e.target.value)}
                            className="gf-input text-sm w-full"
                            rows={3}
                            placeholder="Sector, comentarios, fechas clave…"
                            data-testid="emp-notas"
                        />
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                        <button type="button" onClick={onCancel} disabled={saving} className="btn-outline text-sm justify-center" data-testid="modal-cancel">
                            Cancelar
                        </button>
                        <button type="submit" disabled={saving || !form.razon_social.trim()} className="btn-gold justify-center" data-testid="modal-save">
                            {saving ? "Guardando…" : (form.id ? "Guardar cambios" : "Crear empresa")}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

function Field({ label, value, onChange, testId, type = "text", placeholder, required }) {
    return (
        <div>
            <label className="block text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1.5">{label}</label>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                className="gf-input text-sm w-full"
                data-testid={testId}
            />
        </div>
    );
}
